import { useReactiveVar } from "@apollo/client";
import { useHydrated } from "@tanstack/react-router";
import { useCallback, useContext, useMemo } from "react";

import { CATEGORY_ALL } from "@/components/game-categories/constants";
import { LocationContext } from "@/contexts/location";
import type { LocationType } from "@/helpers/location";
import { exploreCategory } from "@/store";

export default function useCurrentLocation() {
  const [state] = useContext(LocationContext);

  const category = useReactiveVar(exploreCategory);
  const isHydrated = useHydrated();

  // biome-ignore lint/correctness/useExhaustiveDependencies: its fine
  const apiLocationObj = useMemo(() => {
    const { apiLocation } = state;

    if (apiLocation) {
      const address: string[] = [];

      if (apiLocation.city) {
        address.push(apiLocation.city);
      }

      if (apiLocation.country) {
        if (apiLocation.country === "USA") {
          if (apiLocation.region) {
            address.push(apiLocation.region);
          }
        } else {
          address.push(apiLocation.country);
        }
      }

      return {
        address: address.length ? address.join(", ") : "Unknown location",
        location: `${apiLocation.lat},${apiLocation.lng}`,
      };
    }

    return null;
  }, [state.apiLocation]);

  const location = useMemo((): LocationType => {
    if (state.gpsLocation) {
      return state.gpsLocation;
    }

    if (apiLocationObj) {
      return apiLocationObj;
    }

    return { address: "", location: "" };
  }, [apiLocationObj, state.gpsLocation]);

  const toExploreUrl = useCallback(
    (asFormAction = false, customLocation = location) => {
      let url = "/explore";

      if (customLocation.address) {
        url += `/${customLocation.address}`;
      }

      const query: string[] = [];

      if (!asFormAction && customLocation.location) {
        query.push(`location=${customLocation.location}`);
      }

      if (
        !asFormAction &&
        isHydrated &&
        category &&
        category !== CATEGORY_ALL
      ) {
        query.push(`categories=${category}`);
      }

      if (query.length) {
        url += `?${query.join("&")}`;
      }

      return url;
    },
    [location, category, isHydrated]
  );

  return {
    gpsLoading: state.gpsLoading,
    loading: state.apiLoading || state.gpsLoading,
    location,
    toExploreUrl,
  };
}
